import store from 'store';

import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import {
  adminBeginUrl,
  adminPricingToolRoute,
  adminResidualValuesSearchRoute,
  cartDetailsRoute,
  checkoutCartRoute,
  creditRoute,
  customerRoute,
  dashboardUrl,
  extrasRoute,
  monitorUrl,
  productCustomizeRoute,
  productListRoute,
  productOptionsRoute,
  recycleEvaluateOrderRoute,
  recycleEvaluateRoute,
  recycleOutcomesOrderRoute,
  recycleOutcomesRoute,
  recycleSearchOrderRoute,
  recycleSearchRoute,
  servicesUrl,
  servicesUrlAny,
  standardAnyUrl,
  standardFinanceRoute,
  upgradeAnyUrl,
  upgradeFlowRoute,
  changePasswordUrl,
  loginPageUrl,
  loginRecoverUrl,
  loginTokenRoute,
  passwordResetRoute,
} from 'constants/routes';
import { ADMIN, VENDOR_MANAGER } from 'constants/user';

import Dashboard from 'pages/dashboard/Dashboard';
import ServiceMonitor from 'pages/monitor/ServiceMonitor';
import StandardFinancing from 'pages/posale/[financingType]/StandardFinancing';
import Upgrade from 'pages/posale/[financingType]/Upgrade';
import ProductCategories from 'pages/posale/[financingType]/[cartUUID]/ProductCategories';
import ProductList from 'pages/posale/[financingType]/catalog/ProductList';
import CreditSteps from 'pages/posale/[financingType]/credit/CreditSteps';
import CustomerInfo from 'pages/posale/[financingType]/customer/CustomerInfo';
import ProductCustomization from 'pages/posale/[financingType]/customize/ProductCustomization';
import ProductExtras from 'pages/posale/[financingType]/extras/ProductExtras';
import ProductOptions from 'pages/posale/[financingType]/options/ProductOptions';
import AdminPage from 'pages/posale/admin/components/AdminPage';
import PricingTool from 'pages/posale/admin/pricing_tool/PricingTool';
import ResidualValuesSearch from 'pages/posale/admin/residualvalues/ResidualValuesSearch';
import CartDetails from 'pages/posale/cart/[cartUUID]/CartDetails';
import Checkout from 'pages/posale/checkout/[cartUUID]/Checkout';
import Evaluation from 'pages/posale/recycle/[[orderUUID]]/evaluate/Evaluation';
import RecycleOutcomes from 'pages/posale/recycle/[[orderUUID]]/outcomes/RecycleOutcomes';
import RenderRecycleSearch from 'pages/posale/recycle/[[orderUUID]]/search/RenderRecycleSearch';
//TODO remove this component when the multi trade search is completed
import Services from 'pages/posale/services/[[cartUUID]]/Services';
import Login from 'pages/posale/login/Login';
import PasswordForgot from 'pages/posale/login/recover/PasswordForgot';
import PasswordReset from 'pages/posale/login/resetpassword/[id]/PasswordReset';
import PasswordChange from 'pages/posale/password/change/PasswordChange';
import PageNotFound from './PageNotFound';
import PrevLocationUpdater from './PrevLocationUpdater';
import RouteRules from './RouteRules';
import ScrollToTop from './ScrollToTop';
import { withSentryReactRouterV6Routing } from '@sentry/react';

const Router = () => {
  let location = useLocation();
  const { servicesConfig } = store?.getState().whitelabel.configs;
  const { services } = servicesConfig || {};
  const activeFlows = services?.reduce((result, service) => {
    const cv = service?.group?.reduce((acc, item) => {
      return {
        ...acc,
        [item.flow]: item.active && { active: true },
      };
    }, {});

    return {
      ...result,
      ...cv,
    };
  }, {});

  const { UPGRADE, STANDARD, TRADE_IN } = activeFlows || {};

  const SentryRoutes = withSentryReactRouterV6Routing(Routes);

  return (
    <>
      <PrevLocationUpdater location={location} store={store} />

      <SentryRoutes location={location} primary={false}>
        <Route exact element={<Navigate to='/posale/services' />} path='/' />

        <Route element={<ScrollToTop />} path='/'>
          <Route path='*' element={<Navigate to='/posale/services' />} />

          <Route
            path={'/posale'}
            element={<Navigate replace to={servicesUrl} />}
          />

          <Route element={<Login />} path={loginPageUrl} />

          <Route element={<Login />} path={loginTokenRoute} />
          <Route element={<PasswordForgot />} path={loginRecoverUrl} />
          <Route element={<PasswordReset />} path={passwordResetRoute} />
          <Route
            element={<RouteRules component={PasswordChange} />}
            path={changePasswordUrl}
          />

          <Route element={<Dashboard />} path={dashboardUrl} />
          <Route element={<ServiceMonitor />} path={monitorUrl} />

          <Route
            element={<RouteRules component={Services} />}
            path={servicesUrl}
          />

          <Route
            element={<RouteRules component={Services} />}
            path={servicesUrlAny}
          />

          {UPGRADE?.active ? (
            <Route
              element={<RouteRules exact component={Upgrade} />}
              path={upgradeFlowRoute}
            >
              <Route element={<PageNotFound withHeader />} path='*' />

              <Route element={<ProductCategories />} path={upgradeFlowRoute} />
              <Route element={<ProductList />} path={productListRoute} />
              <Route element={<ProductOptions />} path={productOptionsRoute} />
              <Route
                element={<ProductCustomization />}
                path={productCustomizeRoute}
              />
              <Route element={<ProductExtras />} path={extrasRoute} />
              <Route element={<CustomerInfo />} path={customerRoute} />
              <Route element={<CreditSteps />} path={creditRoute} />
            </Route>
          ) : (
            <Route
              path={upgradeAnyUrl}
              element={<Navigate replace to={servicesUrl} />}
            />
          )}
          {STANDARD?.active ? (
            <Route
              element={<RouteRules component={StandardFinancing} />}
              path={standardFinanceRoute}
            >
              <Route element={<PageNotFound withHeader />} path='*' />

              <Route
                element={<ProductCategories />}
                path={standardFinanceRoute}
              />
              <Route element={<ProductList />} path={productListRoute} />
              <Route element={<ProductOptions />} path={productOptionsRoute} />
              <Route
                element={<ProductCustomization />}
                path={productCustomizeRoute}
              />
              <Route element={<ProductExtras />} path={extrasRoute} />
            </Route>
          ) : (
            <Route
              path={standardAnyUrl}
              element={<Navigate replace to={servicesUrl} />}
            />
          )}

          <Route
            element={<RouteRules component={CartDetails} />}
            path={cartDetailsRoute}
          />
          <Route
            element={<RouteRules component={Checkout} />}
            path={checkoutCartRoute}
          >
            <Route element={<PageNotFound withHeader />} path='*' />

            <Route element={<CustomerInfo />} path={customerRoute} />
            <Route element={<CreditSteps />} path={creditRoute} />
          </Route>

          {TRADE_IN?.active ? (
            <Route
              element={<RouteRules component={RenderRecycleSearch} />}
              path={recycleSearchRoute}
            />
          ) : (
            <Route
              path={recycleSearchRoute}
              element={<Navigate replace to={servicesUrl} />}
            />
          )}

          <Route
            element={<RouteRules component={Evaluation} />}
            path={recycleEvaluateRoute}
          />
          <Route
            element={<RouteRules component={Evaluation} />}
            path={recycleEvaluateOrderRoute}
          />

          <Route
            element={<RouteRules component={RecycleOutcomes} />}
            path={recycleOutcomesRoute}
          />
          <Route
            element={<RouteRules component={RecycleOutcomes} />}
            path={recycleOutcomesOrderRoute}
          />

          <Route
            element={<RouteRules component={RenderRecycleSearch} />}
            path={recycleSearchOrderRoute}
          />

          <Route
            element={
              <RouteRules
                allowedRoles={[ADMIN, VENDOR_MANAGER]}
                component={ResidualValuesSearch}
                className='grey-bg'
              />
            }
            path={adminResidualValuesSearchRoute}
          />

          <Route element={<RouteRules />} path={adminBeginUrl}>
            <Route element={<PageNotFound withHeader />} path='*' />

            <Route
              element={
                <AdminPage
                  allowedRoles={[ADMIN, VENDOR_MANAGER]}
                  component={PricingTool}
                />
              }
              path={adminPricingToolRoute}
            />
          </Route>
        </Route>
      </SentryRoutes>
    </>
  );
};

export default Router;
